// /invite/:token — accept a team / owner invitation (the link in the invite
// email). Collects the person's name + a password, creates the account via
// acceptInvite, then sends them to sign in. Shares the AuthLayout chrome with
// Login / ResetPassword. An invalid/expired link never dead-ends — it offers a
// path back to login.

import { type FormEvent, type ReactNode, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { AuthLayout } from './AuthLayout.js'
import { acceptInvite, checkInvite } from './invite-data.js'
import { PASSWORD_RULES, passwordMeetsPolicy } from './reset-password-data.js'
import { Button, FormField, Input, PasswordInput, Spinner } from '../../design-system/index.js'

type View = 'loading' | 'set' | 'done' | 'invalid'

function BackToLogin(): JSX.Element {
  return (
    <p className="mt-5 text-center">
      <Link
        to="/login"
        className="inline-flex items-center gap-1.5 rounded text-[12px] font-semibold text-link hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
      >
        Ir a iniciar sesión
      </Link>
    </p>
  )
}

function strengthLevel(pw: string): 'weak' | 'mid' | 'strong' {
  const met = PASSWORD_RULES.filter((r) => r.test(pw)).length
  return met <= 1 ? 'weak' : met === 2 ? 'mid' : 'strong'
}

function StateBlock({
  tone,
  icon,
  title,
  children,
}: {
  tone: 'ok' | 'warn'
  icon: ReactNode
  title: string
  children: ReactNode
}): JSX.Element {
  const toneClass = tone === 'ok' ? 'bg-ok-bg text-ok' : 'bg-warn-bg text-warn'
  return (
    <div className="text-center">
      <span
        aria-hidden
        className={`mx-auto grid size-[60px] place-items-center rounded-2xl ${toneClass}`}
      >
        {icon}
      </span>
      <h1 className="mt-4 text-[22px] font-bold tracking-[-0.022em] text-text">{title}</h1>
      {children}
    </div>
  )
}

const CHECK_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    className="size-[30px]"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
)

const WARN_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    className="size-[28px]"
  >
    <path d="M12 9v4M12 17h.01M10.3 3.9 2 18a2 2 0 0 0 1.7 3h16.6A2 2 0 0 0 22 18L13.7 3.9a2 2 0 0 0-3.4 0Z" />
  </svg>
)

export function InviteAcceptPage(): JSX.Element {
  const { token = '' } = useParams()
  const navigate = useNavigate()

  const [view, setView] = useState<View>('loading')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // Probe the link on load so a consumed/expired invite shows the "ya no sirve"
  // state right away instead of rendering the form and failing on submit.
  useEffect(() => {
    let alive = true
    void checkInvite(token)
      .then((ok) => {
        if (alive) setView(ok ? 'set' : 'invalid')
      })
      // A probe failure (network/500) must not leave the page stuck on the
      // loading spinner — fall through to the "link no longer works" state.
      .catch(() => {
        if (alive) setView('invalid')
      })
    return () => {
      alive = false
    }
  }, [token])

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      const result = await acceptInvite(token, name.trim(), password)
      if (result === 'ok') setView('done')
      else if (result === 'weak_password')
        setError('La contraseña no cumple los requisitos mínimos.')
      else setView('invalid')
    } catch {
      setError('No se pudo completar. Inténtalo de nuevo.')
    } finally {
      setBusy(false)
    }
  }

  const policyOk = passwordMeetsPolicy(password)
  const matches = password.length > 0 && password === confirm
  const nameOk = name.trim().length > 0

  return (
    <AuthLayout>
      {view === 'loading' && (
        <div className="py-6">
          <Spinner label="Validando tu invitación…" />
        </div>
      )}

      {view === 'set' && (
        <>
          <h1 className="text-center text-[22px] font-bold tracking-[-0.022em] text-text">
            Crea tu cuenta
          </h1>
          <p className="mx-auto mt-2 max-w-[40ch] text-center text-[13px] leading-relaxed text-text-2">
            Elige tu nombre y una contraseña para entrar a tu espacio en INHERTA.
          </p>
          <form onSubmit={(e) => void onSubmit(e)} className="mt-6" noValidate>
            <FormField label="Tu nombre" htmlFor="invite-name">
              <Input
                id="invite-name"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
                placeholder="Nombre y apellido"
              />
            </FormField>
            <div className="mt-3.5">
              <FormField label="Contraseña" htmlFor="invite-password">
                <PasswordInput
                  id="invite-password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                  rules={PASSWORD_RULES.map((r) => ({ label: r.label, met: r.test(password) }))}
                  {...(password.length > 0 ? { strength: strengthLevel(password) } : {})}
                />
              </FormField>
            </div>
            <div className="mt-3.5">
              <FormField label="Confirmar contraseña" htmlFor="invite-confirm">
                <PasswordInput
                  id="invite-confirm"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={busy}
                />
              </FormField>
              {confirm.length > 0 && !matches && (
                <p className="mt-1.5 text-[11.5px] font-medium text-sens">
                  Las contraseñas no coinciden.
                </p>
              )}
            </div>
            {error && (
              <p role="alert" className="mt-3 text-[12px] font-medium text-sens">
                {error}
              </p>
            )}
            <Button
              type="submit"
              block
              className="mt-5"
              loading={busy}
              disabled={!nameOk || !policyOk || !matches}
            >
              Crear mi cuenta
            </Button>
          </form>
          <BackToLogin />
        </>
      )}

      {view === 'done' && (
        <StateBlock tone="ok" icon={CHECK_ICON} title="Tu cuenta está lista">
          <p className="mx-auto mt-2 max-w-[40ch] text-center text-[13px] leading-relaxed text-text-2">
            Creamos tu cuenta. Inicia sesión con tu correo y la contraseña que acabas de elegir.
          </p>
          <div className="mt-5">
            <Button block onClick={() => void navigate('/login')}>
              Ir a iniciar sesión
            </Button>
          </div>
        </StateBlock>
      )}

      {view === 'invalid' && (
        <>
          <StateBlock tone="warn" icon={WARN_ICON} title="Este enlace ya no sirve">
            <p className="mx-auto mt-2 max-w-[40ch] text-center text-[13px] leading-relaxed text-text-2">
              La invitación venció o ya se usó. Pídele a quien te invitó que te envíe una nueva.
            </p>
            <div className="mt-5">
              <Button block onClick={() => void navigate('/login')}>
                Ir a iniciar sesión
              </Button>
            </div>
          </StateBlock>
        </>
      )}
    </AuthLayout>
  )
}
